<template>
  <config-base showPlanDetails class="opc-webinar-trial" ref="baseRef" :on="$listeners">
    <div slot="configDesc" class="opc-webinar-trial__configDesc" ref="configDesc">
      <p class="opc-webinar-trial__desc-text">
        <img src="@/assets/plans/checked_checkboxes-green.svg" alt="Include" />
        {{ $t('billing.webinar.trial.freeTrialDescriptions.duration', [trialDays]) }}
      </p>
      <p class="opc-webinar-trial__desc-text">
        <img src="@/assets/plans/checked_checkboxes-green.svg" alt="" />
        {{ $t('billing.webinar.trial.freeTrialDescriptions.capacity', [capacity]) }}
      </p>
      <p class="opc-webinar-trial__desc-text">
        <img src="@/assets/plans/checked_checkboxes-green.svg" alt="" />
        {{ $t('billing.webinar.trial.freeTrialDescriptions.cancel') }}
      </p>
      <p class="opc-webinar-trial__additional-message">
        {{ $t('billing.webinar.trial.requirement') }}
      </p>
    </div>
    <template #config>
      <div class="divide-line" />
      <capacity-config-list
        :config-plan-type="planType"
        :config-capacity-key="capacityKey"
        :configRowName="configRowName"
        capacityUnitI18nKey="billing.attendees"
      ></capacity-config-list>
    </template>
  </config-base>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import planCommon from '@/plans/WEBINAR/mixin';
import plansMixin from '@/plans/mx/plan';
import { plansof } from '@/utils/chain';
import { getPlanTotalLicense } from '@/common/service/validate';
import FreeTrialTracking from '@/plans/mx/free-trial-tracking.js';
import { planUnitMap } from '@/common/product-unit';
import ConfigList from '@/components/capacity/config-list.vue';
import ConfigBase from '@/plans/WEBINAR/config/base.vue';
import MixedPlans from '../mixins/mixed-plans';
import TwoWayControl from '@/plans/WEBINAR/mixins/two-way-control';
import { orderByPlanListWithCapacity } from '@/components/capacity/config-list.vue';
import { trackingPlanTypes, planTypes } from '@/common/constants';
import { SUBSCRIPTION_TYPE } from '@/common/constants';
import { isEmptyVal } from '@/utils';

const DEFAULT_WEBINAR_FREE_TRIAL_DAYS = 14; // FREE TRIAL - default free trial days

const ConfigBaseComp = {
  name: 'webinar-free-base',
  extends: ConfigBase,
  mixins: [FreeTrialTracking],
  props: {
    showPlanDetails: Boolean,
  },
  data() {
    return {
      unit: planUnitMap.license,
    };
  },
  computed: {
    trackingPlanKey() {
      return `${_.get(trackingPlanTypes, _.get(planTypes, this.planType))} free trial`;
    },
  },
  methods: {
    // FREE TRIAL - {plan title} Free Trial
    mx_renderTitle() {
      const isShowCheckIcon = this.hasSelectedPlan;

      return (
        <h2 class="opc-zm-base-card__name text-capitalize">
          {this.$t('billing.trial.title', [this.title])}
          {isShowCheckIcon ? <i check-icon class="zm-icon-success main-icon" aria-hidden="true" /> : null}
        </h2>
      );
    },
    mx_renderDivideLine() {
      return <div class="divide-line-transparent" />;
    },
  },
};

const CapacityConfigList = {
  name: 'CapacityConfigList',
  extends: ConfigList,
  mixins: [planCommon, MixedPlans, TwoWayControl],
  computed: {
    ...mapGetters(['getCurrentMeetingPlans', 'getCurrentBasePlanInfo']),
    addAnotherCapacityCta() {
      return ''; // Free trial - remove the add cta
    },
    allCapacityOrderList() {
      // Filter free trial capacity
      return orderByPlanListWithCapacity(
        this.planList.filter((plan) => !!plan.isOnlineFreeTrial),
        this.configCapacityKey,
      );
    },
    planList() {
      return this.planMapFlat
        .filter((p) => p.type === this.planType)
        .filter((p) => {
          const isSupportCapacity = this.supportCapacity.includes(p[this.capacityKey]);
          return isSupportCapacity;
        });
    },
    mx_showSelectCapacityDropdown() {
      return this.allCapacityOrderList.length !== 1;
    },
    currentBasePlans() {
      return this.getCurrentMeetingPlans.length ? this.getCurrentMeetingPlans : this.getCurrentBasePlanInfo?.plans;
    },
  },
  methods: {
    mx_getBillingCycleGroupOptions(_, capacity) {
      let subOptions = [];
      subOptions = this.mx_getBillingCycleList(capacity).filter((option) => !!option?.plan?.isOnlineFreeTrial); // Filter free trial only;
      const { capacityDescList } = this.getCapacityByDesc();
      return [
        {
          label: this.getCapacityGroupLabel(
            capacityDescList /* ['500','500','500','1000','1000','1000',... ] */
              .filter((item) =>
                this.allCapacityOrderList /* [500] */
                  .map(String)
                  .includes(item),
              ) /* Filter free trial only capacity */,
          ),
          options: subOptions,
          type: SUBSCRIPTION_TYPE.recurring_and_one_month,
        },
      ];
    },
    getCapacityGroupLabel(list = []) {
      // For future - it could be more than 1 capacity
      return list.length && list[0] !== list[list.length - 1]
        ? this.$t('billing.single_use_meeting.a_to_b_attendees', [list[0], list[list.length - 1]])
        : this.$t('billing.events.onlyForAttendeesDesc', [+list[0]]);
    },
    // For future - it could be more than 1 license, even now it's invisible
    mx_getMaxHosts() {
      return getPlanTotalLicense(this.currentBasePlans || []) || 999;
    },
  },
};
export default {
  mixins: [plansMixin, planCommon],
  components: { ConfigBase: ConfigBaseComp, CapacityConfigList },
  data() {
    return {
      trialDays: DEFAULT_WEBINAR_FREE_TRIAL_DAYS,
      capacity: 500,
      selectedNumber: 1,
      billingCycle: '',
      trackingPlanName: trackingPlanTypes.WEBINAR,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'planMapFlat', 'getUserCurrentSubscriptions']),
    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },
    configRowName() {
      return 'CapacityConfigRowV2FreeTrial';
    },
  },
  created() {
    const selectedPlan = this.selectedPlan;
    if (!isEmptyVal(selectedPlan)) {
      this.trialDays = selectedPlan?.trialDays ?? this.trialDays;
      this.capacity = selectedPlan?.capacity ?? this.capacity;
      this.billingCycle = selectedPlan.billing_cycle;
      this.selectedNumber = selectedPlan.cartInfo?.selected_number;
    }
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
// ZOOM-900958
html:not(:lang(en-US)), html:not(:lang(zh-CN)), html:not(:lang(zh-TW)) {
  .opc-webinar-trial {
    .zm-collapse-item:not(:has(.is-active)) {
      .opc-zm-base-card__name {
        max-width: 386px;
      }
    }
  }
  @include media(mobile) {
    .opc-webinar-trial {
      .zm-collapse-item:not(:has(.is-active)) {
        .opc-zm-base-card__name {
          max-width: 45vw;
        }
      }
    }
  }
}

.divide-line-transparent {
  width: 100%;
  border-top: 1px solid transparent;
  margin: 6px 0;
}
.opc-webinar-trial {
  &__configDesc {
    padding-left: 60px;
  }
  &__additional-message {
    margin-top: 20px;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    color: #6e7680;
  }
  &__desc-text {
    display: flex;
    align-items: center;
    color: #00053d;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    margin: 4px 0;
    img {
      display: inline-block;
      margin-right: 4px;
    }
  }
}

@include media(mobile) {
  .opc-webinar-trial {
    .opc-foldable .opc-zm-base-card__name {
        // Adding a small space for icon
        width: calc(55vw + 20px);
    }
    &__configDesc {
      padding-left: 5px;
    }
    &__desc-text {
      margin: 8px 0;
    }
  }
}
</style>
